import CircleSpinner from 'components/circle-spinner/CircleSpinner'
import ErrorBox from 'components/message-boxes/error-box/ErrorBox'
import { MSP_ACTIONS, MspDispatchContext } from 'components/multistep/Multistep'
import {
	ApplicationDispatchContext,
	LOAN_APPLICATION_ACTIONS,
} from 'contexts/ApplicationContext'
import {
	validateFormDataAction,
	formKeySelector,
	IWithForms,
} from 'formBuilder/src'
import { TranslationFile } from 'i18n'
import React, { useContext, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useDispatch, useStore } from 'react-redux'
import updateApplicationServices from 'services/updateApplicationServices'
import { FillPersonalDataUK } from '../FillPersonalData/FillPersonalDataUK'
import { IApplicationContext } from 'interfaces/i-application-context-props'
import { Image } from 'react-bootstrap'
import avarda_card from 'img/credit-card-images/avarda_card.png'
import { stringifyNum } from 'utils/commonhelpers'
import { InfoBox } from 'components/message-boxes/information-box/InformationBox'
import { SummaryBoxModal } from 'components/modals/summary-box/SummaryBoxModal'

const ApplicationDetailsUk = () => {
	const { t } = useTranslation([
		TranslationFile.Translation,
		TranslationFile.Forms,
	])
	const { loanApplication }: IApplicationContext = useContext(
		ApplicationDispatchContext,
	)
	const { msp, dispatchMsp } = useContext(MspDispatchContext)
	const dispatch = useDispatch()
	const store = useStore()
	const [loading, setLoading] = useState<boolean>(false)
	const [error, setError] = useState<Error | any>()
	const [editMode, setEditMode] = useState<boolean>(false)
	const [showSummaryBoxModal, setShowSummaryBoxModal] =
		useState<boolean>(false)

	const continueBtnClick = async () => {
		if (editMode) await submitApplicationData()
		else {
			dispatchMsp({
				type: MSP_ACTIONS.SET_INDEX,
				payload: {
					index: msp.index + 1,
				},
			})
		}
	}

	const editBtnClick = async () => {
		setEditMode(!editMode)
	}

	const submitApplicationData = async () => {
		dispatch(validateFormDataAction('applicationUpdateForm'))

		const errors = formKeySelector(
			'applicationUpdateForm',
			'errors',
		)(store.getState() as IWithForms)
		if (errors?.length) {
			setTimeout(() => {
				document
					.querySelector<HTMLElement>('.has-error')
					?.scrollIntoView({ behavior: 'smooth', block: 'start' })
			}, 100)
			return
		}

		// TODO: add some fixed values to the application data, like we do for DE?
		setLoading(true)
		setError(null)

		dispatchMsp({
			type: MSP_ACTIONS.SET_INDEX,
			payload: {
				index: msp.index + 1,
			},
		})
	}

	return (
		<>
			{showSummaryBoxModal && (
				<SummaryBoxModal
					applicationInformation={loanApplication.applicationData}
					showModal={true}
					handleClose={() => {
						setShowSummaryBoxModal(false)
					}}
				/>
			)}
			<div className="application-details-uk">
				<span className="pre-approved-badge">
					{t('ApplicationDataViewPreApprovedBadge')}
				</span>
				<div className="multistep-content">
					<h2 className="multistep-header">
						<Trans
							i18nKey="ApplicationDataViewHeader"
							values={{
								firstName:
									loanApplication.applicationData
										?.personalInfo?.firstName,
								lastName:
									loanApplication.applicationData
										?.personalInfo?.lastName,
							}}
						/>
					</h2>
				</div>
				<br />
				<div>
					<p>{t('ApplicationDataViewDescription')}</p>
				</div>
				<br />
				<div>
					<h3>{t('OfferSegmentHeader')}</h3>
				</div>

				<div className="avarda-offer-box">
					<div className="avarda-offer-box-image">
						<Image
							src={avarda_card}
							className="credit-card-gold"
							alt="TF Bank Mastercard Gold"
						/>
					</div>
					<div className="avarda-offer-box-content">
						<div className="avarda-offer-box-info">
							<div className="avarda-offer-box-info-item">
								<div className="avarda-offer-box-info-header">
									Representative APR
								</div>
								<div className="avarda-offer-box-info-value">
									{stringifyNum(
										loanApplication.applicationData?.apr,
										{
											style: 'percent',
										},
									)}
								</div>
							</div>
							<div className="avarda-offer-box-info-item">
								<div className="avarda-offer-box-info-header">
									Interest rate for purchases (pa)
								</div>
								<div className="avarda-offer-box-info-value">
									{stringifyNum(
										loanApplication.applicationData
											?.interestRate,
										{
											style: 'percent',
										},
									)}
								</div>
							</div>
							<div className="avarda-offer-box-info-item">
								<div className="avarda-offer-box-info-header">
									Estimated credit limit
								</div>
								<div className="avarda-offer-box-info-value">
									£
									{stringifyNum(
										loanApplication.applicationData
											?.creditLimit,
										{
											useGrouping: true,
											minimumFractionDigits: 0,
										},
									)}
								</div>
							</div>
						</div>
						<div className="avarda-offer-box-summary">
							<button
								className="btn btn-link-gray-11"
								onClick={() => setShowSummaryBoxModal(true)}>
								{t('ViewSummaryBox')}
							</button>
						</div>
					</div>
				</div>
				<InfoBox
					infoType="info"
					css="avarda-info avarda-bullet-list"
					message={
						<>
							<strong>{t('YourApplicationStatusHeader')}</strong>
							<br />
							<ul>
								<li>{t('YourApplicationStatusLine1')}</li>
								<li>{t('YourApplicationStatusLine2')}</li>
								<li>{t('YourApplicationStatusLine3')}</li>
								<li>{t('YourApplicationStatusLine4')}</li>
							</ul>
						</>
					}
				/>
				<br />
				<div>
					<h2>{t('UpdateApplication')}</h2>
					<div>
						<p>{t('ApplicationDetailsDescription')}</p>
					</div>
					{loading ? (
						<div className="col-spinner">
							<CircleSpinner />
						</div>
					) : error ? (
						<ErrorBox message={t('ErrorMsg')} />
					) : (
						<>
							<FillPersonalDataUK {...{ locked: !editMode }} />
								<div className="step-actions">
									<button
										type="button"
										disabled={loading}
										className="btn btn-secondary"
										onClick={() => {
											setTimeout(() => {
												editBtnClick()
											}, 400)
										}}
									>
										{editMode ? t('CancelEdit') : t('Edit')}
									</button>

									<button
										type="button"
										disabled={loading}
										className="btn btn-primary"
										onClick={() => {
											setTimeout(() => {
												continueBtnClick()
											}, 400)
										}}
									>
										{editMode ? t('SaveContinue') : t('Continue')}
									</button>
								</div>
						</>
					)}
				</div>
			</div>
		</>
	)
}
export default ApplicationDetailsUk